import React, { useMemo } from 'react';
import { withRouter } from 'react-router-dom';

import Table from 'components/common/table';
import { CustomCheckbox } from 'components/common/checkbox';

import useTitle from 'hooks/useTitle';

import ChangeLeaveIssuerFilters from './components/ChangeLeaveIssuerFilters';
import ChangeLeaveIssuerRejectModal from './components/ChangeLeaveIssuerRejectModal';
import { useChangeLeaveIssuerList } from './useChangeLeaveIssuerList';
import { getChangeLeaveIssuerColumns } from './changeLeaveIssuerColumns';

import type { ChangeLeaveIssuerListProps } from './interfaces';
import type { LeaveIssuerChangeRequestItem } from 'interface/issuer';

const ChangeLeaveIssuerList: React.FC<ChangeLeaveIssuerListProps> = (props) => {
  useTitle('Leave Issuer Change Requests');

  const {
    filters,
    setFilters,
    isLoading,
    requestSummary,
    dataToShow,
    selectedRequestIds,
    setSelectedRequestIds,
    isBulkApproving,
    setIsSelectAllChecked,
    setRowData,
    isRejectingRequest,
    isExporting,
    isModalOpen,
    setIsModalOpen,
    isFilterChanged,
    setIsFilterChanged,
    isFilterApplied,
    isStartDateChanged,
    isEndDateChanged,
    isAscendingOrdered,
    setIsAscendingOrdered,
    columnToSortBy,
    setColumnToSortBy,
    handleReset,
    handleApplyFilter,
    handleExportClick,
    handleRejectClick,
    handleApproveClick,
    handleBulkApproveClick,
    onChangeSelectAllCheckbox,
    isChecked,
    isStatusApprovedOrRejected,
  } = useChangeLeaveIssuerList(props);

  const columns = useMemo(
    () =>
      getChangeLeaveIssuerColumns({
        selectedRequestIds,
        setSelectedRequestIds,
        setIsSelectAllChecked,
        isAscendingOrdered,
        setIsAscendingOrdered,
        columnToSortBy,
        setColumnToSortBy,
        isRejectingRequest,
        handleApproveClick,
        openRejectModal: (row) => {
          setIsModalOpen(true);
          setRowData(row);
        },
      }),
    [
      columnToSortBy,
      handleApproveClick,
      isAscendingOrdered,
      isRejectingRequest,
      selectedRequestIds,
      setIsSelectAllChecked,
      setSelectedRequestIds,
      setRowData,
      setIsModalOpen,
      setColumnToSortBy,
      setIsAscendingOrdered,
    ]
  );

  return (
    <main>
      <ChangeLeaveIssuerRejectModal
        isOpen={isModalOpen}
        isRejectingRequest={isRejectingRequest}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleRejectClick}
      />

      <div className="container">
        <div className="full-scope-card">
          <ChangeLeaveIssuerFilters
            isLoading={isLoading}
            requestSummary={requestSummary}
            filters={filters}
            setFilters={setFilters}
            isFilterChanged={isFilterChanged}
            isFilterApplied={isFilterApplied}
            setIsFilterChanged={setIsFilterChanged}
            isStartDateChanged={isStartDateChanged}
            isEndDateChanged={isEndDateChanged}
            handleApplyFilter={handleApplyFilter}
            handleReset={handleReset}
            handleExportClick={handleExportClick}
            isExporting={isExporting}
            hasData={Boolean(dataToShow.length)}
            onBulkApprove={handleBulkApproveClick}
            isBulkApproving={isBulkApproving}
            hasSelectedRequests={Boolean(selectedRequestIds.length)}
          />

          <div className="full-scope-card__content p-relative">
            <Table<LeaveIssuerChangeRequestItem>
              loading={isLoading}
              data={dataToShow}
              columns={columns}
              showPagination={false}
              noDataText="Leave Issuer Change Requests not found."
              customClassName="leave-issuer-list-table rt-cursor-default"
            />
            <CustomCheckbox
              disabled={isStatusApprovedOrRejected}
              onChange={onChangeSelectAllCheckbox}
              checked={isChecked}
              className="position-absolute topx-14 leftx-16"
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default withRouter(ChangeLeaveIssuerList);
